<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useThemeStore, type ThemeMode } from '../stores/theme'

/**
 * ThemeToggle — single button that cycles through light → dark → system.
 *
 * Why a single cycling button (not a 3-way segmented control)?
 *   - Topbar real-estate is tight; cycling matches the GitHub / VS Code mental
 *     model users already know.
 *   - Keyboard-only: one focus stop instead of three.
 *   - The current icon + tooltip make the "what will I switch to next?"
 *     question answerable in <1s.
 *
 * Accessibility:
 *   - `aria-label` always announces the current mode (screen reader reads
 *     "Switch theme (current: dark)" so the user knows what state they're in
 *     before pressing).
 *   - `title` provides a friendlier hover hint.
 *   - Focus ring uses the design-token `--ring` so it inherits the
 *     theme's accent color in both light and dark.
 */

const store = useThemeStore()
// storeToRefs preserves reactivity in the template — destructuring `store`
// directly would freeze `mode` to its initial value.
const { mode } = storeToRefs(store)

// Drives which SVG is rendered. Mirrors `mode` 1:1 — kept as a computed for
// readability in the template and to leave room for future remapping (e.g.,
// could split 'system' into 'system-light' vs 'system-dark' if we ever want
// the monitor icon to hint at the resolved theme).
const iconKey = computed<'light' | 'dark' | 'system'>(() => mode.value)

const ariaLabel = computed(() => `Switch theme (current: ${mode.value})`)

// A Record rather than a switch, matching `stores/theme.ts`'s own cycle map. The
// switch was exhaustive today and TypeScript agreed, but only because ThemeMode
// happens to have three members: adding a fourth would have compiled and
// silently returned `undefined` here. A Record makes the omission a type error
// at the point of adding the mode, and drops a lint finding that could not see
// the types to know the switch was safe.
const TOOLTIP_BY_MODE: Record<ThemeMode, string> = {
  light: 'Light mode',
  dark: 'Dark mode',
  system: 'System (auto)',
}

const tooltipTitle = computed(() => TOOLTIP_BY_MODE[mode.value])
</script>

<template>
  <button
    type="button"
    class="theme-toggle"
    :aria-label="ariaLabel"
    :title="tooltipTitle"
    data-testid="theme-toggle"
    @click="store.cycle()"
  >
    <!-- Sun: central disk + 8 rays -->
    <svg
      v-if="iconKey === 'light'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="M4.93 4.93l1.41 1.41" />
      <path d="M17.66 17.66l1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="M4.93 19.07l1.41-1.41" />
      <path d="M17.66 6.34l1.41-1.41" />
    </svg>

    <!-- Moon: crescent -->
    <svg
      v-else-if="iconKey === 'dark'"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>

    <!-- Monitor: screen + stand (system / auto) -->
    <svg
      v-else
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  </button>
</template>

<style scoped>
.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: transparent;
  color: var(--text-2);
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    border-color 150ms ease,
    color 150ms ease;
}

.theme-toggle:hover {
  background: var(--ctl-bg);
  border-color: var(--line);
}

.theme-toggle:focus-visible {
  outline: none;
  box-shadow: var(--ring);
}

.theme-toggle svg {
  width: 18px;
  height: 18px;
  display: block;
}
</style>